import { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../api';

const initials = (n) => n ? n.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase() : '?';

export default function Layout() {
  const auth = useAuth();
  const themeCtx = useTheme();
  const user = auth?.user || null;
  const logout = auth?.logout;
  const theme = themeCtx?.theme || 'light';
  const toggle = themeCtx?.toggle;
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [stamped, setStamped] = useState(null);
  const [now, setNow] = useState(new Date());
  const [stampLoading, setStampLoading] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    api.get('/zeiterfassung/status').then(r => setStamped(r.data?.aktiv || null)).catch(() => {});
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  let stampTime = '--:--';
  if (stamped && stamped.einstempel_utc) {
    // Z1: laufende Dauer aus dem absoluten UTC-Moment - Wallclock-Arithmetik
    // waere bei Nachtschicht ueber Mitternacht, DST-Umstellung oder
    // Zonenwechsel falsch/negativ.
    const diffMin = Math.max(0, Math.floor((now.getTime() - new Date(stamped.einstempel_utc).getTime()) / 60000));
    stampTime = `${Math.floor(diffMin / 60)}h ${diffMin % 60}m`;
  } else if (stamped && stamped.einstempel) {
    // Legacy-Fallback (Alt-Eintrag ohne einstempel_utc): bisherige Rechnung.
    const [h1, m1] = stamped.einstempel.split(':').map(Number);
    const diff = (now.getHours() * 60 + now.getMinutes()) - (h1 * 60 + m1);
    stampTime = `${Math.floor(diff / 60)}h ${diff % 60}m`;
  }

  const toggleStamp = async () => {
    setStampLoading(true);
    let lat = null, lng = null, adresse = null;
    try {
      if (navigator.geolocation) {
        const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 5000 }));
        lat = pos.coords.latitude;
        lng = pos.coords.longitude;
        try {
          const r = await api.get(`/geocode?lat=${lat}&lng=${lng}`);
          adresse = r.data.adresse;
        } catch {}
      }
    } catch {}

    try {
      // Z1: Geraete-Zeitzone (IANA) mitsenden - der Server bestimmt den
      // Zeitpunkt und leitet die Wallclock in DIESER Zone ab. Keine
      // Hardcodierung, kein Server-/Railway-Bezug.
      const tz = (Intl.DateTimeFormat().resolvedOptions().timeZone) || undefined;
      if (stamped) {
        await api.post('/zeiterfassung/ausstempeln', { lat, lng, adresse, tz });
        setStamped(null);
      } else {
        const r = await api.post('/zeiterfassung/einstempeln', { lat, lng, adresse, tz });
        // Anzeige aus der SERVER-Response (gespeicherte Ortszeit-Wallclock)
        // statt Browser-toTimeString - damit ist die Sidebar sofort
        // konsistent mit dem Zustand nach einem Reload (/status).
        setStamped({ id: r.data.id, einstempel: r.data.einstempel, einstempel_utc: r.data.einstempel_utc, adresse_ein: adresse });
      }
    } catch {
      alert('Fehler bei der Zeiterfassung');
    }
    setStampLoading(false);
  };

  const handleLogout = (e) => {
    e.preventDefault();
    if (typeof logout === 'function') logout();
  };

  const handleToggleTheme = () => {
    if (typeof toggle === 'function') toggle();
  };

  const navLinkClass = ({ isActive }) => 'nav-link' + (isActive ? ' active' : '');
  const tabLinkClass = ({ isActive }) => 'tab-item' + (isActive ? ' active' : '');

  const titles = {
    '/': 'Dashboard',
    '/bautagebuch': 'Bautagebuch',
    '/material': 'Material',
    '/baustellen': 'Baustellen',
    '/team': 'Team',
    '/zeiterfassung': 'Zeiterfassung',
    '/auswertung': 'Baustellen-Auswertung',
    '/projektfortschritt': 'Projektfortschritt',
    '/projektpositionen': 'Projektpositionen',
    '/lv-import': 'LV-Import',
    '/stammdaten': 'Stammdaten',
  };
  const currentTitle = titles[location.pathname.split('/').slice(0, 2).join('/')] || 'DV Elektro';

  return (
    <div className="app-layout">
      <div className={`sidebar-overlay ${open ? 'open' : ''}`} onClick={() => setOpen(false)} />

      <aside className={`sidebar ${open ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">DV <span>ELEKTRO</span></div>
          <div className="sidebar-subtitle">Mitarbeiterportal</div>
        </div>

        <div className="sidebar-user">
          <div className="avatar">{initials(user?.name)}</div>
          <div>
            <div className="user-name">{user?.name || ''}</div>
            <div className="user-role">{user?.role === 'admin' ? 'Administrator' : 'Monteur'}</div>
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">Hauptmenü</div>
          <NavLink to="/" end className={navLinkClass}>🏠 Dashboard</NavLink>
          <NavLink to="/bautagebuch" className={navLinkClass}>📋 Bautagebuch</NavLink>
          <NavLink to="/material" className={navLinkClass}>📦 Material</NavLink>

          <div className="nav-section">Verwaltung</div>
          <NavLink to="/baustellen" className={navLinkClass}>🏗️ Baustellen</NavLink>
          {user?.role === 'admin' && (
            <>
              <NavLink to="/zeiterfassung" className={navLinkClass}>⏱️ Zeiterfassung</NavLink>
              <NavLink to="/auswertung" className={navLinkClass}>📊 Auswertung</NavLink>
              <NavLink to="/projektfortschritt" className={navLinkClass}>📈 Projektfortschritt</NavLink>
              <NavLink to="/projektpositionen" className={navLinkClass}>📋 Projektpositionen</NavLink>
              <NavLink to="/lv-import" className={navLinkClass}>📄 LV-Import</NavLink>
              <NavLink to="/team" className={navLinkClass}>👥 Team</NavLink>
              <NavLink to="/stammdaten" className={navLinkClass}>🛠️ Stammdaten</NavLink>
            </>
          )}

          <button type="button" className="nav-link" onClick={handleLogout}
            style={{ cursor: 'pointer', marginTop: 8, background: 'none', border: 'none', width: '100%', textAlign: 'left', font: 'inherit', color: 'inherit' }}>
            ↪ Abmelden
          </button>
        </nav>

        <div className="sidebar-footer">
          <div className="checkin-card">
            <div className="checkin-status">{stamped ? 'Eingestempelt seit' : 'Nicht eingestempelt'}</div>
            <div className="checkin-time">{stamped ? stampTime : '--:--'}</div>
            {stamped?.adresse_ein && <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 4, lineHeight: 1.3 }}>📍 {stamped.adresse_ein}</div>}
          </div>
          <button type="button" className={`btn-checkin ${stamped ? 'aus' : 'ein'}`} onClick={toggleStamp} disabled={stampLoading}>
            {stampLoading ? '⏳ Lädt…' : stamped ? 'Ausstempeln' : 'Einstempeln'}
          </button>
        </div>
      </aside>

      <div className="main-area">
        <header className="topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button type="button" className="burger-btn" onClick={() => setOpen(true)}>☰</button>
            <div className="topbar-title">{currentTitle}</div>
          </div>
          <div className="topbar-actions">
            <button type="button" className="theme-toggle" onClick={handleToggleTheme} title={theme === 'light' ? 'Dunkler Modus' : 'Heller Modus'}>
              {theme === 'light' ? '🌙' : '☀️'}
            </button>
          </div>
        </header>

        <main className="page-content">
          <Outlet />
        </main>
      </div>

      <nav className="tabbar">
        <NavLink to="/" end className={tabLinkClass}><span className="tab-icon">🏠</span>Start</NavLink>
        <NavLink to="/bautagebuch" className={tabLinkClass}><span className="tab-icon">📋</span>Tagebuch</NavLink>
        <NavLink to="/material" className={tabLinkClass}><span className="tab-icon">📦</span>Material</NavLink>
        <NavLink to="/baustellen" className={tabLinkClass}><span className="tab-icon">🏗️</span>Baustellen</NavLink>
      </nav>
    </div>
  );
}
